import React, { useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { Play, Pause, Volume2, VolumeX, Maximize } from 'lucide-react';
import "../styles/VideoGallery.css";

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return "0:00";
  const total = Math.floor(seconds);
  const hh = Math.floor(total / 3600);
  const mm = Math.floor((total % 3600) / 60);
  const ss = (total % 60).toString().padStart(2, "0");
  if (hh) return `${hh}:${mm.toString().padStart(2, "0")}:${ss}`;
  return `${mm}:${ss}`;
};

// Reproductor de video utilizando ReactPlayer
const VideoPlayer = ({ video }) => {
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const [played, setPlayed] = useState(0);
  const [duration, setDuration] = useState(0);
  const playerRef = useRef(null);
  const stageRef = useRef(null);

  // Al cambiar de clip, volver al inicio y esperar al usuario
  useEffect(() => {
    setPlayed(0);
    setDuration(0);
    setPlaying(false);
  }, [video.src]);

  const handleSeekChange = (e) => {
    const value = parseFloat(e.target.value);
    setPlayed(value);
    if (playerRef.current) playerRef.current.seekTo(value, 'fraction');
  };

  const handleFullscreen = () => {
    const node = stageRef.current;
    if (!node) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if (node.requestFullscreen) {
      node.requestFullscreen();
    }
  };

  return (
    <div className="vplayer">
      <div className="vplayer__stage" ref={stageRef}>
        <ReactPlayer
          ref={playerRef}
          url={video.src}
          width="100%"
          height="100%"
          playing={playing}
          volume={volume}
          muted={muted}
          onProgress={(state) => setPlayed(state.played)}
          onDuration={setDuration}
          onEnded={() => setPlaying(false)}
          progressInterval={100}
          config={{
            file: {
              attributes: {
                controlsList: 'nodownload',
                playsInline: true
              }
            }
          }}
        />

        <button
          type="button"
          className="vplayer__overlay"
          onClick={() => setPlaying(true)}
          hidden={playing}
          aria-label={`Play ${video.title}`}
        >
          <span>
            <Play size={30} fill="currentColor" aria-hidden="true" />
          </span>
        </button>
      </div>

      <div className="vplayer__controls">
        <button
          type="button"
          className="vplayer__btn"
          onClick={() => setPlaying((value) => !value)}
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? (
            <Pause size={18} fill="currentColor" aria-hidden="true" />
          ) : (
            <Play size={18} fill="currentColor" aria-hidden="true" />
          )}
        </button>

        <input
          type="range"
          className="vplayer__range vplayer__seek"
          min={0}
          max={0.999999}
          step="any"
          value={played}
          onChange={handleSeekChange}
          style={{ '--fill': `${played * 100}%` }}
          aria-label="Seek"
        />

        <span className="vplayer__time">
          {formatTime(duration * played)} / {formatTime(duration)}
        </span>

        <button
          type="button"
          className="vplayer__btn vplayer__btn--quiet"
          onClick={() => setMuted((value) => !value)}
          aria-label={muted ? "Unmute" : "Mute"}
        >
          {muted || volume === 0 ? (
            <VolumeX size={16} aria-hidden="true" />
          ) : (
            <Volume2 size={16} aria-hidden="true" />
          )}
        </button>

        <input
          type="range"
          className="vplayer__range vplayer__volume"
          min={0}
          max={1}
          step="any"
          value={muted ? 0 : volume}
          onChange={(e) => {
            setVolume(parseFloat(e.target.value));
            setMuted(false);
          }}
          style={{ '--fill': `${(muted ? 0 : volume) * 100}%` }}
          aria-label="Volume"
        />

        <button
          type="button"
          className="vplayer__btn vplayer__btn--quiet"
          onClick={handleFullscreen}
          aria-label="Full screen"
        >
          <Maximize size={16} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default VideoPlayer;
